<script lang="ts">
  // The composer's suggested change: a code editor on the whole file at the
  // head (CodeMirror, loaded on first use and colored by the engine), opened
  // on the selected lines, and the live diff of what the edits suggest. A
  // plain text box stands in if the editor cannot load. Tab and Shift-Tab
  // indent and outdent; Esc returns to the comment and keeps the
  // suggestion; only "Remove suggestion" drops it.
  import { onMount } from 'svelte';
  import type { Span } from '../engine/types';
  import type { SuggestEditor } from '../editor/suggestEditor';
  import { highlightText, languageOf } from '../diff/snippet';
  import { indentLines, indentUnitOf, suggestionLines } from '../logic/suggestion';
  import type { Hunk, SuggestPlan } from '../logic/suggestEdit';
  import SuggestionDiff from './SuggestionDiff.svelte';
  import { editorKey } from '../keys/editorKey';

  interface Props {
    path: string;
    /** The file at the head, which the edits start from. */
    head: readonly string[];
    /** The lines the comment was started on (1-based, inclusive): opened on, and marked. */
    selected: { start: number; end: number };
    /** Lines in the diff, which take suggestions: marked. */
    hunks: readonly Hunk[];
    /** The head's highlight, when known, so the editor opens colored. */
    spans: Span[][] | null;
    /** The edited file's starting text. */
    text: string;
    /** What the edits make (from the parent, which posts it). */
    plan: SuggestPlan;
    onchange: (text: string) => void;
    /** Cmd/Ctrl+Enter. */
    onsubmit: () => void;
    /** Esc: focus goes back to the comment text; the suggestion stays. */
    onescape: () => void;
    /** "Remove suggestion". */
    onremove: () => void;
  }
  let { path, head, selected, hunks, spans, text: initial, plan, onchange, onsubmit, onescape, onremove }: Props = $props();

  // svelte-ignore state_referenced_locally
  let text = $state(initial);
  let host: HTMLDivElement | undefined = $state();
  let status: 'loading' | 'ready' | 'failed' = $state('loading');
  let fallback: HTMLTextAreaElement | undefined = $state();
  /** The editor's colors for the edited file (one highlight per edit, shared with the diffs below). */
  let colors: { lines: readonly string[]; spans: Span[][] | null } = $state.raw({ lines: [], spans: null });
  // svelte-ignore state_referenced_locally
  const unit = indentUnitOf(head.slice(Math.max(0, selected.start - 1), selected.end));
  /** Highlighting a whole file waits longer after an edit than a few lines would. */
  const DEBOUNCE_MS = 300;
  const range = (s: number, e: number) => (s === e ? `line ${s}` : `lines ${s}–${e}`);
  const label = $derived(`Suggested change: the file at the head, opened at ${range(selected.start, selected.end)}`);

  function changed(t: string) {
    text = t;
    onchange(t);
  }

  onMount(() => {
    let editor: SuggestEditor | null = null;
    let gone = false;
    void Promise.all([import('../editor/suggestEditor'), languageOf(path)])
      .then(([m, language]) => {
        if (gone || !host) return;
        editor = m.createSuggestEditor({
          parent: host,
          doc: text,
          selected,
          inDiff: hunks,
          spans: text === head.join('\n') ? spans : null,
          indentUnit: unit,
          label,
          highlight: (t) => highlightText(language, t),
          debounceMs: DEBOUNCE_MS,
          onspans: (t, s) => (colors = { lines: suggestionLines(t), spans: s }),
          onchange: changed,
          onsubmit,
          onescape,
        });
        status = 'ready';
        editor.focus();
      })
      .catch((e) => {
        console.warn('suggestion editor unavailable; using a text box', e);
        status = 'failed';
        queueMicrotask(() => {
          if (!fallback) return;
          // Opened at the selected lines, as the code editor is.
          const at = head.slice(0, selected.start - 1).reduce((n, l) => n + l.length + 1, 0);
          fallback.setSelectionRange(at, at);
          fallback.focus();
          const lineHeight = parseFloat(getComputedStyle(fallback).lineHeight) || 18;
          fallback.scrollTop = Math.max(0, (selected.start - 3) * lineHeight);
        });
      });
    return () => {
      gone = true;
      editor?.destroy();
    };
  });

  /** The text box's keys, as the code editor has them (see `editorKey`). */
  function keydown(e: KeyboardEvent) {
    const box = e.currentTarget as HTMLTextAreaElement;
    const k = editorKey(e);
    if (!k) return;
    if (k === 'submit') onsubmit();
    else if (k === 'escape') onescape();
    else {
      const next = indentLines(box.value, box.selectionStart, box.selectionEnd, k === 'outdent', unit);
      box.value = next.text;
      box.setSelectionRange(next.start, next.end);
      changed(next.text);
    }
    e.preventDefault();
    e.stopPropagation();
  }

  /** The editor's colors for `count` edited lines from index `at`, for a diff's "+" side. */
  function colorsAt(at: number, count: number) {
    if (status === 'failed') return undefined;
    return { lines: colors.lines.slice(at, at + count), spans: colors.spans?.slice(at, at + count) ?? null };
  }

  /** Each part of the preview: the head lines it replaces, what replaces them, and where. */
  const parts = $derived.by(() => {
    switch (plan.kind) {
      case 'unchanged': {
        const lines = head.slice(selected.start - 1, selected.end);
        return [{ start: selected.start, end: selected.end, original: lines, lines, at: selected.start - 1, outside: false }];
      }
      case 'one':
        return [{ ...plan.suggestion, outside: false }];
      case 'split':
        return plan.suggestions.map((s) => ({ ...s, outside: false }));
      case 'outside':
        return plan.regions.map((r) => ({ ...r, original: head.slice(r.start - 1, r.end), outside: plan.outside.includes(r) }));
    }
  });
</script>

<div class="suggest-editor" data-suggest-editor>
  <div class="row">
    <span class="where">Edit anywhere in the file · the suggestion covers the lines you change · lines in the diff are marked green at the left · Tab indents · Esc returns to the comment</span>
    <span class="spacer"></span>
    <button class="micro" onclick={onremove} title="Remove the suggested change; the comment text stays">Remove suggestion</button>
  </div>
  {#if status === 'failed'}
    <textarea
      bind:this={fallback}
      class="suggest-fallback"
      aria-label={label}
      spellcheck="false"
      value={text}
      oninput={(e) => changed(e.currentTarget.value)}
      onkeydown={keydown}
    ></textarea>
  {:else}
    <div class="suggest-host" bind:this={host}>{#if status === 'loading'}<span class="where">Loading editor…</span>{/if}</div>
  {/if}
  {#each parts as part, i (i)}
    <div class="suggest-part" class:outside={part.outside}>
      {#if parts.length > 1 || plan.kind !== 'unchanged'}
        <span class="where suggest-part-lines"
          >{plan.kind === 'outside' ? 'Edit' : 'Suggestion'}{parts.length > 1 ? ` ${i + 1} of ${parts.length}` : ''} · replaces {range(part.start, part.end)}{part.outside
            ? ' · outside the diff'
            : ''}</span
        >
      {/if}
      <SuggestionDiff {path} original={part.original} suggested={part.lines} suggestedColors={colorsAt(part.at, part.lines.length)} />
    </div>
  {/each}
</div>
